/**
 * Copyright (c) 2019-2021 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { createComputeRenderable } from '../../renderable';
import { createComputeRenderItem } from '../../webgl/render-item';
import { TextureSpec, UniformSpec } from '../../renderable/schema';
import { ShaderCode } from '../../../mol-gl/shader-code';
import { ValueCell } from '../../../mol-util';
import { QuadSchema, QuadValues } from '../util';
import { getTriCount } from './tables';
import { quad_vert } from '../../../mol-gl/shader/quad.vert';
import { activeVoxels_frag } from '../../../mol-gl/shader/marching-cubes/active-voxels.frag';

var ActiveVoxelsSchema = __assign(__assign({}, QuadSchema), {
  tTriCount: TextureSpec('image-uint8', 'alpha', 'ubyte', 'nearest'),
  tVolumeData: TextureSpec('texture', 'rgba', 'ubyte', 'nearest'),
  uIsoValue: UniformSpec('f'),
  uGridDim: UniformSpec('v3'),
  uGridTexDim: UniformSpec('v3'),
  uScale: UniformSpec('v2')
});

var ActiveVoxelsName = 'active-voxels';

function getActiveVoxelsRenderable(ctx, volumeData, gridDim, gridTexDim, isoValue, scale) {
  if (ctx.namedComputeRenderables[ActiveVoxelsName]) {
    var v = ctx.namedComputeRenderables[ActiveVoxelsName].values;
    ValueCell.update(v.uQuadScale, scale);
    ValueCell.update(v.tVolumeData, volumeData);
    ValueCell.updateIfChanged(v.uIsoValue, isoValue);
    ValueCell.update(v.uGridDim, gridDim);
    ValueCell.update(v.uGridTexDim, gridTexDim);
    ValueCell.update(v.uScale, scale);
    ctx.namedComputeRenderables[ActiveVoxelsName].update();
  } else {
    ctx.namedComputeRenderables[ActiveVoxelsName] = createActiveVoxelsRenderable(ctx, volumeData, gridDim, gridTexDim, isoValue, scale);
  }

  return ctx.namedComputeRenderables[ActiveVoxelsName];
}

function createActiveVoxelsRenderable(ctx, volumeData, gridDim, gridTexDim, isoValue, scale) {
  var values = __assign(__assign({}, QuadValues), {
    tTriCount: ValueCell.create(getTriCount()),
    uQuadScale: ValueCell.create(scale),
    tVolumeData: ValueCell.create(volumeData),
    uIsoValue: ValueCell.create(isoValue),
    uGridDim: ValueCell.create(gridDim),
    uGridTexDim: ValueCell.create(gridTexDim),
    uScale: ValueCell.create(scale)
  });

  var schema = __assign({}, ActiveVoxelsSchema);

  var shaderCode = ShaderCode('active-voxels', quad_vert, activeVoxels_frag);
  var renderItem = createComputeRenderItem(ctx, 'triangles', shaderCode, schema, values);
  return createComputeRenderable(renderItem, values);
}

function setRenderingDefaults(ctx) {
  var gl = ctx.gl,
      state = ctx.state;
  state.disable(gl.CULL_FACE);
  state.disable(gl.BLEND);
  state.disable(gl.DEPTH_TEST);
  state.enable(gl.SCISSOR_TEST);
  state.depthMask(false);
  state.colorMask(true, true, true, true);
  state.clearColor(0, 0, 0, 0);
}

export function calcActiveVoxels(ctx, volumeData, gridDim, gridTexDim, isoValue, gridScale) {
  var gl = ctx.gl,
      resources = ctx.resources;
  var width = volumeData.getWidth();
  var height = volumeData.getHeight();

  if (!ctx.namedFramebuffers[ActiveVoxelsName]) {
    ctx.namedFramebuffers[ActiveVoxelsName] = resources.framebuffer();
  }

  var framebuffer = ctx.namedFramebuffers[ActiveVoxelsName];
  framebuffer.bind();

  if (!ctx.namedTextures[ActiveVoxelsName]) {
    ctx.namedTextures[ActiveVoxelsName] = resources.texture('image-uint8', 'rgba', 'ubyte', 'nearest');
  }

  var activeVoxelsTex = ctx.namedTextures[ActiveVoxelsName];
  activeVoxelsTex.define(width, height);
  var renderable = getActiveVoxelsRenderable(ctx, volumeData, gridDim, gridTexDim, isoValue, gridScale);
  ctx.state.currentRenderItemId = -1;
  activeVoxelsTex.attachFramebuffer(framebuffer, 0);
  setRenderingDefaults(ctx);
  gl.viewport(0, 0, width, height);
  gl.scissor(0, 0, width, height);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.scissor(0, 0, gridTexDim[0], gridTexDim[1]);
  renderable.render(); // console.log('gridScale', gridScale, 'gridTexDim', gridTexDim, 'gridDim', gridDim);
  // console.log('volumeData', volumeData);
  // console.log('at', readTexture(ctx, activeVoxelsTex));

  gl.finish();
  return activeVoxelsTex;
}